import { Box, IconButton, Popover, Typography } from '@mui/material';
import { getSelectorAttribute, headerSelectors } from '@vy/app-selectors';
import { SheetWithText } from '@vy/ui-design-system';
import { isNullOrUndefined } from '@vy/utils';
import { FC, useEffect, useRef } from 'react';
import { useUpdateNotificationsReadStatus } from '../../../../hooks/requests/use-update-notifications-read-status';
import { AnalyticCardEmptyState } from '../../../../pages/home-v2/components/EmptyState/AnalyticCardEmptyState';
import { CloseIcon } from '../../../icons/CloseIcon';
import { VyDivider } from '../../atoms';
import { NotificationCardProps, VyNotificationCard } from '../VyNotificationCard/VyNotificationCard';

export type NotificationCenterProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  notifications: NotificationCardProps[];
};

export const VyNotificationCenter: FC<NotificationCenterProps> = ({
  anchorEl,
  open,
  onClose,
  notifications,
}) => {
  const { mutate } = useUpdateNotificationsReadStatus();
  const hasMutatedRef = useRef(false);

  const unreadNotifications = notifications.filter((notification) => isNullOrUndefined(notification.readAt));
  const unreadCount = unreadNotifications.length;

  const noNotifications = notifications.length === 0;

  useEffect(() => {
    if (!open) {
      hasMutatedRef.current = false;
      return;
    }

    if (unreadNotifications.length === 0 || hasMutatedRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      const unreadIds = unreadNotifications.map((n) => n.id);

      hasMutatedRef.current = true;

      mutate({
        ids: unreadIds,
        status: 'READ',
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [open, mutate]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 382,
            height: 645,
            borderRadius: '12px',
            boxShadow: '0px 8px 24px 0px rgba(12, 18, 69, 0.08)',
            border: '1px solid #F0F4F6',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: 'black', mb: '2px' }}>
            Notification center
          </Typography>
          <Typography sx={{ fontSize: '14px', color: 'black' }}>
            {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <IconButton size="small" onClick={onClose}>
          <CloseIcon color="#323232" style={{ transform: 'scale(0.8)' }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          marginTop: '16px',
        }}
      >
        {noNotifications ? (
          <AnalyticCardEmptyState
            title="You're all caught up"
            description="We'll let you know when there's something new to see"
            icon={<SheetWithText />}
          />
        ) : (
          notifications.map((notification) => (
            <Box
              key={notification.id}
              {...getSelectorAttribute(headerSelectors.notifications.notificationCard)}
            >
              <VyNotificationCard {...notification} />
              <VyDivider sx={{ my: '14px' }} />
            </Box>
          ))
        )}
      </Box>
    </Popover>
  );
};
